<template>
  <div class="picker-container">
    <component
      :is="conditionConfigComponent"
      :condition="condition"
      :options="conditionOptions"
      @changed="conditionUpdated"
      @completed="onConfigChanged"
    />
  </div>
</template>

<script lang="ts" setup>
import { ConditionViewModel } from './models/conditionViewModel';
import { IConditionsManager } from './models/conditionsManager';
import { ConditionOperationType } from '@/shared/components/conditions/models/conditions';
import { PropType, ref, onMounted, computed } from 'vue';

const props = defineProps({
  value: {
    type: Object as PropType<ConditionViewModel>,
    required: true,
  },
  manager: {
    type: Object as PropType<IConditionsManager>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'updated', condition: ConditionViewModel): void;
  (e: 'completed', condition: ConditionViewModel): void;
}>();

const condition = ref<ConditionViewModel>({
  attribute: '',
  operation: '',
  type: '',
  unit: '',
  value: '',
  result: true,
});

onMounted(() => {
  if (props.value) {
    condition.value = {
      attribute: props.value.attribute,
      operation: props.value.operation,
      type: mappedType.value,
      unit: props.value.unit,
      value: props.value.value,
      result: props.value.result,
      invalid: props.value.invalid,
      isNew: props.value.isNew,
    };
  }
});

const mappedType = computed(() => {
  const mappings: { [key: string]: string } = {
    seconds: 'duration',
  };
  return mappings[props.value.type] != null ? mappings[props.value.type] : props.value.type;
});

const conditionConfigComponent = computed(() => {
  return props.manager.getConfigComponent(condition.value);
});

const conditionOptions = computed(() => {
  return props.manager.getConditionOptions(condition.value);
});

function conditionUpdated(update: ConditionViewModel) {
  // pass up any changes as they happen, so that the parent has the up-to-date state
  // if the config is closed by clicking away
  if (update.operation === ConditionOperationType.IsSet) {
    update.value = '';
    update.unit = undefined;
  }

  condition.value = update;
  emit('updated', condition.value);
}

function onConfigChanged(update: ConditionViewModel) {
  // let the parent know that the config has been closed by clicking the done button
  condition.value = update;
  emit('completed', condition.value);
}
</script>

<style scoped lang="scss">
.button {
  background-color: transparent;
  border: solid 1px #cccccc;
  color: inherit;

  &:hover {
    color: inherit;
    border-color: #666666;
  }

  &:focus {
    color: inherit;
    border-color: inherit;
  }

  &:focus:not(:active) {
    box-shadow: 0 0 0 0.125em rgba(#666666, 0.25);
  }
}

.picker-container {
  position: absolute;
  min-width: 350px;
  background: white;
  top: 4px;
  z-index: 10;

  border-radius: 4px;
  box-shadow:
    0 3px 8px 0 rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(10, 10, 10, 0.1);
  padding-bottom: 0.5rem;
  padding-top: 0.5rem;
  padding: 0.5rem;
  max-height: 400px;
  overflow: auto;

  visibility: visible;

  &.hidden {
    visibility: hidden;
  }
}

.control {
  margin-top: 0.75rem;
}
</style>
